import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { PERMISSION_DATA } from "api/ApiPath";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { CapabilityDetailEntity } from "features/capabilityManagement/domain/entities/CapabilityDetailEntity";
import { UserPermissionInjection } from "features/userPermissionManagement/di/UserPermissionInjection";
import { UserPermissionActionRequestEntity } from "features/userPermissionManagement/domain/entities/actions/UserPermissionActionRequestEntity";
import { UserPermissionCapabilityActionRequestEntity } from "features/userPermissionManagement/domain/entities/actions/UserPermissionCapabilityActionRequestEntity";
import { DetailUserPermissionEntity } from "features/userPermissionManagement/domain/entities/detail/DetailUserPermissionEntity";
import { UserPermissionRequestEntity } from "features/userPermissionManagement/domain/entities/UserPermissionRequestEntity";
import { UserPermissionResponseEntity } from "features/userPermissionManagement/domain/entities/UserPermissionResponseEntity";
import { RefObject } from "react";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";

class UserPermissionInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public fetchList = new BehaviorSubject<UserPermissionRequestEntity>({});
  public fetchDetail = new BehaviorSubject<UserPermissionRequestEntity>({});
  public fetchUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchMenus = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    action?: "create" | "update";
    value?: UserPermissionActionRequestEntity;
    detail?: DetailUserPermissionEntity;
    userList?: Array<BaseUserResponseModel>;
  }>({});
  public fetchCapabilityActions = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    value?: UserPermissionCapabilityActionRequestEntity;
    permissionCode?: string;
    detail?: CapabilityDetailEntity;
  }>({});
  public fetchCapabilityDetail = new BehaviorSubject<{ code?: string }>({});
  public fetchDelete = new BehaviorSubject<UserPermissionRequestEntity>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchUsers.complete();
    this.fetchMenus.complete();
    this.fetchActions.complete();
    this.fetchCapabilityActions.complete();
    this.fetchCapabilityDetail.complete();
    this.fetchDelete.complete();
  }
}

class UserPermissionOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<UserPermissionResponseEntity>>(
    [],
  );
  public savedMenus = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedDetail = new BehaviorSubject<DetailUserPermissionEntity>({});
  public savedCapabilityDetail = new BehaviorSubject<CapabilityDetailEntity>(
    {},
  );
  public savedPaging = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onActionSuccess = new BehaviorSubject<string>("");
  public onCapabilityActionSuccess = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedUsers.complete();
    this.savedMenus.complete();
    this.savedDetail.complete();
    this.savedCapabilityDetail.complete();
    this.savedPaging.complete();
    this.onError.complete();
    this.onActionSuccess.complete();
    this.onCapabilityActionSuccess.complete();
  }
}

@injectable()
export class UserPermissionViewModel extends BaseViewModel<
  UserPermissionInput,
  UserPermissionOutput
> {
  constructor() {
    super(new UserPermissionInput(), new UserPermissionOutput());
  }

  private userPermissionUseCase = UserPermissionInjection.getUseCase();
  private filterUseCase = FilterInjection.getUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: UserPermissionRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : "createdAt DESC";

    const query = {
      ...prevParams,
      ...formValues,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    return {
      query,
      pagination: {
        page: pagination?.current - 1,
        limit: pagination?.pageSize,
      },
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params, guard) => {
      try {
        guard?.runIfLatest(() => this.input.loading.next(true));
        const res = await this.userPermissionUseCase.getList(params);
        this.output.savedList.next(res?.data || []);
        this.output.savedPaging.next(res?.pagination || {});
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.savedPaging.next({});
        this.output.onError.next(error);
      } finally {
        guard?.runIfLatest(() => this.input.loading.next(false));
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.loading.next(true);
        const res =
          await this.userPermissionUseCase.getPermissionDetail(params);
        this.output.savedDetail.next(res?.data || {});
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchUsers, async (params) => {
      try {
        const res = await this.userPermissionUseCase.getUsers(params);
        this.output.savedUsers.next(res?.data || []);
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchCapabilityDetail, async (params) => {
      try {
        const res =
          await this.userPermissionUseCase.getCapabilityDetail(params);
        if (res?.data) {
          this.output.savedCapabilityDetail.next(res.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (payload) => {
      try {
        this.input.loading.next(true);

        if (payload?.action === "create" && payload?.value) {
          const res = await this.userPermissionUseCase.createPermission(
            payload.value,
            payload.detail || {},
          );
          if (res?.data) {
            this.output.onActionSuccess.next("create");
          }
        }

        if (payload?.action === "update" && payload?.value && payload?.detail) {
          const res = await this.userPermissionUseCase.updatePermission(
            payload.value,
            payload.detail,
            payload?.userList,
          );
          if (res?.data) {
            this.output.onActionSuccess.next("update");
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(
      this.input.fetchCapabilityActions,
      async (payload) => {
        try {
          this.input.loading.next(true);

          if (
            payload?.action === "create" &&
            payload?.value &&
            payload?.permissionCode
          ) {
            const res = await this.userPermissionUseCase.createCapability(
              payload.permissionCode,
              payload.value,
              payload.detail || {},
            );
            if (res?.data) {
              this.output.onCapabilityActionSuccess.next("create");
            }
          }

          if (
            payload?.action === "update" &&
            payload?.value &&
            payload?.permissionCode
          ) {
            const res = await this.userPermissionUseCase.updateCapability(
              payload.permissionCode,
              payload.value,
              payload.detail || {},
            );
            if (res?.data) {
              this.output.onCapabilityActionSuccess.next("update");
            }
          }
          if (payload?.action === "delete" && payload?.value) {
            const res = await this.userPermissionUseCase.deleteCapability(
              payload?.value,
            );
            if (res?.data) {
              this.output.onCapabilityActionSuccess.next("delete");
            }
          }
        } catch (error) {
          console.error(error);
          this.output.onError.next(error);
        } finally {
          this.input.loading.next(false);
        }
      },
    );

    this.skipFirstInitial(this.input.fetchMenus, async (params) => {
      try {
        const res = await this.filterUseCase.fetchListWithFeature({
          path: PERMISSION_DATA.FILTER_LIST,
          params,
          isNeedFeature: true,
          needExpired: true,
          savedKey: CacheKeyEnum.filterMenu,
        });
        if (res) {
          this.output.savedMenus.next(res);
        }
      } catch (error) {}
    });

    this.skipFirstInitial(this.input.fetchDelete, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.userPermissionUseCase.deletePermission(params);
        if (res?.data) {
          this.output.onActionSuccess.next("delete");
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });
  }
}
